import type { IncomingMessage, ServerResponse } from 'node:http'
import type { Socket } from 'node:net'
import { createHash } from 'node:crypto'
import type { AgentConfig, AuditEntry, MultiAgentProxyConfig, ProxyConfig, SecretsStore } from './types.js'
import type { LeafCertCache } from './ca-store.js'
import { evaluateRules } from './matcher.js'
import { AuthError, verifyAgentAuth } from './auth.js'
import { GrantsClient } from './grants-client.js'
import { writeAudit } from './audit.js'
import { checkEgress } from './ssrf.js'
import { handleConnect } from './connect.js'

/**
 * Minimal fetch shape used for upstream calls. `typeof fetch` itself carries a
 * `preconnect` static (Node 22+) which we don't use, so we narrow to the call
 * signature to keep `MultiAgentProxyDeps.fetchImpl` easy to override in tests.
 */
export type UpstreamFetch = (input: Request | URL | string, init?: RequestInit) => Promise<Response>

/**
 * Optional dependencies injected into `createMultiAgentProxy`. Kept additive so
 * existing single-call sites (legacy `createProxy`, `createNodeHandler`) keep
 * working without supplying any of these.
 */
export interface MultiAgentProxyDeps {
  /**
   * In-memory secret lookup. When set, the forward-proxy fetch path consults
   * the store right before forwarding and injects a header on a target match.
   */
  secretsStore?: SecretsStore
  /**
   * Override the upstream fetch implementation. Defaults to `globalThis.fetch`.
   * Tests can pass a stub here when they need to assert the outgoing request
   * shape without relying on `vi.spyOn(globalThis, 'fetch')`.
   */
  fetchImpl?: UpstreamFetch
  /**
   * Daemon mode: identity is implicit (loaded from `~/.config/apes/auth.json`
   * at startup) so per-request `Proxy-Authorization` JWT extraction is
   * skipped. The daemon's identity = `config.agents[0].email`. Inline mode
   * (`apes proxy --` ephemeral case) leaves this unset and keeps the existing
   * JWT-required path.
   */
  daemonMode?: boolean
}

/**
 * Compute a request hash that uniquely identifies the intent.
 * hash = sha256(METHOD + " " + FULL_URL + "\n" + BODY)
 * This binds the grant to the exact request — no bait-and-switch.
 */
async function computeRequestHash(method: string, targetUrl: string, body: ArrayBuffer | null): Promise<string> {
  const hash = createHash('sha256')
  hash.update(`${method} ${targetUrl}\n`)
  if (body && body.byteLength > 0) {
    hash.update(new Uint8Array(body))
  }
  return hash.digest('hex')
}

/**
 * Build the per-agent GrantsClient map used by both the HTTP forward-proxy
 * handler (in this file's `createMultiAgentProxy`) and the CONNECT handler
 * (in `connect.ts`). Exposed so `createNodeHandler` can share a single map
 * instead of letting each handler construct its own — keeps any future
 * per-agent token state coherent.
 */
export function buildGrantsClients(config: MultiAgentProxyConfig): Map<string, GrantsClient> {
  const grantsClients = new Map<string, GrantsClient>()
  for (const agent of config.agents) {
    grantsClients.set(agent.email, new GrantsClient(agent.idp_url))
  }
  return grantsClients
}

/** Multi-agent proxy with SSRF protection and mandatory auth */
export function createMultiAgentProxy(
  config: MultiAgentProxyConfig,
  grantsClients: Map<string, GrantsClient> = buildGrantsClients(config),
  deps: MultiAgentProxyDeps = {},
) {
  // Backwards-compat: if caller didn't pre-build the map, we build our own.
  // createNodeHandler always passes one in so the CONNECT handler can share it.
  for (const agent of config.agents) {
    if (!grantsClients.has(agent.email)) {
      grantsClients.set(agent.email, new GrantsClient(agent.idp_url))
    }
  }

  const mandatoryAuth = config.proxy.mandatory_auth ?? false
  const secretsStore = deps.secretsStore
  const daemonMode = deps.daemonMode ?? false
  // Bind to globalThis so `vi.spyOn(globalThis, 'fetch')` in tests intercepts
  // the upstream call. Resolved per-call so spies installed after construction
  // still take effect.
  const fetchImpl: UpstreamFetch = deps.fetchImpl ?? ((input, init) => globalThis.fetch(input, init))

  return {
    port: Number.parseInt(config.proxy.listen.split(':')[1] || '9090'),
    hostname: config.proxy.listen.split(':')[0] || '127.0.0.1',

    async fetch(req: Request): Promise<Response> {
      const url = new URL(req.url)
      const startTime = Date.now()

      // Health endpoint
      if (url.pathname === '/healthz') {
        return Response.json({
          status: 'ok',
          agents: config.agents.map(a => a.email),
        })
      }

      // Parse target URL from the path
      const targetUrl = url.pathname.slice(1) + url.search
      let targetParsed: URL
      try {
        targetParsed = new URL(targetUrl)
      }
      catch {
        return new Response(
          'Invalid target URL. Send requests as: http://proxy:port/https://target.com/path',
          { status: 400 },
        )
      }

      const domain = targetParsed.hostname
      const method = req.method
      const path = targetParsed.pathname

      // SSRF / reachability check. Split outcomes so a non-existent host
      // doesn't ship as a misleading 403:
      //   - `private`      → policy refusal (403).
      //   - `unresolvable` → upstream connectivity failure (502).
      const egress = await checkEgress(domain)
      if (egress.kind === 'private') {
        return new Response('Blocked: private/loopback IP', { status: 403 })
      }
      if (egress.kind === 'unresolvable') {
        return new Response(
          `DNS lookup failed for ${domain} (${egress.reason}).`,
          { status: 502 },
        )
      }

      // Read body once (needed for hash + forwarding)
      const bodyBuffer = req.body ? await req.arrayBuffer() : null

      // Verify agent identity — find IdP URL from first agent (for JWKS verification)
      // In multi-agent mode, we need the JWT to identify the agent first.
      // We try verification against each agent's IdP until one succeeds.
      let agentIdentity: { email: string, act: 'agent' } | null = null
      if (daemonMode) {
        // Identity is implicit — the daemon was started by/for one specific
        // agent. The first (and only) agent in config.agents is the daemon's
        // identity, loaded from `~/.config/apes/auth.json` at startup.
        agentIdentity = { email: config.agents[0]!.email, act: 'agent' }
      }
      else {
        try {
          for (const agentConf of config.agents) {
            agentIdentity = await verifyAgentAuth(
              req.headers.get('proxy-authorization'),
              agentConf.idp_url,
              mandatoryAuth && config.agents.length === 1,
            )
            if (agentIdentity) break
          }

          // If mandatory auth and no identity found from any IdP
          if (mandatoryAuth && !agentIdentity) {
            throw new AuthError('JWT required')
          }
        }
        catch (err) {
          if (err instanceof AuthError) {
            return new Response(`Unauthorized: ${err.message}`, { status: 401 })
          }
          throw err
        }
      }

      // Find the matching agent config
      const agentEmail = agentIdentity?.email
      let agentConf: AgentConfig | undefined

      if (agentEmail) {
        agentConf = config.agents.find(a => a.email === agentEmail)
        if (!agentConf) {
          return new Response(`Forbidden: unknown agent ${agentEmail}`, { status: 403 })
        }
      }
      else if (config.agents.length === 1) {
        // Non-mandatory auth, single agent: use the only agent config
        agentConf = config.agents[0]! // length === 1 checked above
      }
      else {
        return new Response('Unauthorized: JWT required for multi-agent proxy', { status: 401 })
      }

      const effectiveEmail = agentEmail ?? agentConf.email
      const grantsClient = grantsClients.get(agentConf.email)!
      // The verified agent JWT, forwarded to the IdP on grant requests
      // (#1276): the grants API requires bearer auth and binds the grant's
      // requester to the token subject.
      const rawAgentToken = agentIdentity
        ? req.headers.get('proxy-authorization')?.match(/^Bearer (.+)$/i)?.[1]
        : undefined

      // Build a ProxyConfig-shaped object for evaluateRules
      const rulesConfig: ProxyConfig = {
        proxy: {
          listen: config.proxy.listen,
          idp_url: agentConf.idp_url,
          agent_email: agentConf.email,
          default_action: config.proxy.default_action,
        },
        allow: agentConf.allow ?? [],
        deny: agentConf.deny ?? [],
        grant_required: agentConf.grant_required ?? [],
      }

      // Evaluate rules
      const action = evaluateRules(rulesConfig, domain, method, path)

      const baseAudit: Omit<AuditEntry, 'action' | 'rule'> = {
        ts: new Date().toISOString(),
        agent: effectiveEmail,
        domain,
        method,
        path,
      }

      // DENY
      if (action.type === 'deny') {
        writeAudit({ ...baseAudit, action: 'deny', rule: 'deny-list', grant_id: null })
        return new Response(`Blocked: ${action.note || 'deny rule'}`, { status: 403 })
      }

      // ALLOW (no grant needed)
      if (action.type === 'allow') {
        writeAudit({ ...baseAudit, action: 'allow', rule: 'allow-list', grant_id: null })
        return forwardRequest(req, targetUrl, targetParsed, bodyBuffer, secretsStore, fetchImpl)
      }

      // GRANT REQUIRED
      const rule = action.rule
      const permissions = rule.permissions ?? [`${method.toLowerCase()}:${domain}`]

      // Compute request hash — binds grant to exact method + URL + body
      const requestHash = await computeRequestHash(method, targetUrl, bodyBuffer)

      // Check for existing grant
      const existing = await grantsClient.findExistingGrant(
        effectiveEmail,
        domain,
        'proxy',
        permissions,
      ).catch(() => null)

      if (existing) {
        writeAudit({
          ...baseAudit,
          action: 'grant_approved',
          rule: 'standing-grant',
          grant_id: existing.id,
          request_hash: requestHash,
        })
        return forwardRequest(req, targetUrl, targetParsed, bodyBuffer, secretsStore, fetchImpl)
      }

      // No existing grant — behavior depends on default_action
      if (config.proxy.default_action === 'block') {
        writeAudit({ ...baseAudit, action: 'deny', rule: 'no-grant (block mode)', grant_id: null })
        return new Response('No grant — blocked', { status: 403 })
      }

      if (config.proxy.default_action === 'request-async') {
        // Create grant request, return 407 immediately
        const grant = await grantsClient.requestGrant({
          requester: effectiveEmail,
          targetHost: domain,
          audience: 'ape-proxy',
          grantType: rule.grant_type,
          permissions,
          reason: `${method} ${targetUrl}`,
          requestHash,
          duration: rule.duration,
          agentToken: rawAgentToken,
        }).catch(() => null)

        writeAudit({
          ...baseAudit,
          action: 'grant_denied',
          rule: 'grant_required (async)',
          grant_id: grant?.id ?? null,
        })

        return new Response(
          JSON.stringify({
            error: 'Grant required',
            grant_id: grant?.id,
            message: 'Grant request created. Retry after approval.',
          }),
          { status: 407, headers: { 'Content-Type': 'application/json' } },
        )
      }

      // BLOCKING mode: create grant request and wait
      console.error(`[proxy] Requesting grant for ${method} ${domain}${path} — waiting for approval...`)

      try {
        const grant = await grantsClient.requestGrant({
          requester: effectiveEmail,
          targetHost: domain,
          audience: 'ape-proxy',
          grantType: rule.grant_type,
          permissions,
          reason: `${method} ${targetUrl}`,
          requestHash,
          duration: rule.duration,
          agentToken: rawAgentToken,
        })

        const approved = await grantsClient.waitForApproval(grant.id)

        const waitedMs = Date.now() - startTime

        if (approved.status === 'approved') {
          writeAudit({
            ...baseAudit,
            action: 'grant_approved',
            rule: 'grant_required',
            grant_id: approved.id,
            request_hash: requestHash,
            waited_ms: waitedMs,
          })
          return forwardRequest(req, targetUrl, targetParsed, bodyBuffer, secretsStore, fetchImpl)
        }

        writeAudit({
          ...baseAudit,
          action: 'grant_denied',
          rule: 'grant_required',
          grant_id: approved.id,
          waited_ms: waitedMs,
        })
        return new Response(`Grant denied by ${approved.decided_by}`, { status: 403 })
      }
      catch (err) {
        const msg = err instanceof Error ? err.message : 'Unknown error'
        writeAudit({
          ...baseAudit,
          action: 'grant_timeout',
          rule: 'grant_required',
          error: msg,
        })
        return new Response(`Grant request failed: ${msg}`, { status: 504 })
      }
    },
  }
}

/**
 * Optional dependencies forwarded by `createNodeHandler` to both the
 * forward-proxy fetch path (`createMultiAgentProxy`) and the CONNECT path
 * (`handleConnect`). Kept optional so existing zero-arg callers (legacy
 * single-agent paths, simple multi-agent setups without secret injection or
 * MITM) keep working unchanged.
 */
export interface NodeHandlerDeps {
  /** In-memory secret lookup, forwarded to both the fetch and CONNECT paths. */
  secretsStore?: SecretsStore
  /**
   * Per-host leaf cert cache backed by the daemon's CA bundle. Required (in
   * combination with `secretsStore`) for CONNECT-MITM secret injection on
   * HTTPS targets; absent → CONNECT stays an opaque TCP tunnel.
   */
  leafCache?: LeafCertCache
  /**
   * Daemon mode: skip per-request `Proxy-Authorization` JWT extraction on
   * both the forward-proxy fetch path and the CONNECT path. Forwarded to
   * `createMultiAgentProxy` and `handleConnect`. Inline (ephemeral) mode
   * leaves this unset.
   */
  daemonMode?: boolean
}

/**
 * Create a node:http compatible handler for use with http.createServer().
 * Returns both a request handler and a CONNECT handler.
 */
export function createNodeHandler(config: MultiAgentProxyConfig, deps: NodeHandlerDeps = {}): {
  handleRequest: (req: IncomingMessage, res: ServerResponse) => void
  handleConnect: (req: IncomingMessage, socket: Socket, head: Buffer) => void
} {
  // Build grantsClients once; share with both forward-proxy fetch path
  // (createMultiAgentProxy) and the CONNECT path (handleConnect). Without
  // sharing, CONNECT-time grant_required rules couldn't reach the IdP in
  // multi-agent mode without duplicating constructor work.
  const grantsClients = buildGrantsClients(config)
  const proxy = createMultiAgentProxy(config, grantsClients, {
    secretsStore: deps.secretsStore,
    daemonMode: deps.daemonMode,
  })

  return {
    handleRequest(req: IncomingMessage, res: ServerResponse) {
      // Standard HTTP_PROXY clients (curl, gh, git, npm) send the target as
      // an absolute URL in the request line for cleartext forward-proxying:
      //   `GET http://example.com/path HTTP/1.1`
      // node:http surfaces that absolute URL via `req.url`. The legacy
      // `proxy.fetch` extraction expects a path-encoded form
      // (`/<full-target-url>`), so we adapt here: when `req.url` is
      // absolute, prefix it with a slash so `pathname.slice(1)` recovers
      // the same target string. Path-form clients (legacy) keep working.
      const reqUrl = req.url || '/'
      const isAbsolute = reqUrl.startsWith('http://') || reqUrl.startsWith('https://')
      const proxyHost = req.headers.host || 'localhost'
      const url = isAbsolute
        ? `http://${proxyHost}/${reqUrl}`
        : `http://${proxyHost}${reqUrl}`
      const chunks: Buffer[] = []

      req.on('data', (chunk: Buffer) => chunks.push(chunk))
      req.on('end', async () => {
        try {
          const body = chunks.length > 0 ? Buffer.concat(chunks) : undefined
          const headers = new Headers()
          for (const [key, value] of Object.entries(req.headers)) {
            if (value) {
              headers.set(key, Array.isArray(value) ? value.join(', ') : value)
            }
          }

          const request = new Request(url, {
            method: req.method,
            headers,
            body: body && req.method !== 'GET' && req.method !== 'HEAD' ? body : undefined,
            duplex: 'half',
          } as RequestInit)

          const response = await proxy.fetch(request)

          res.writeHead(response.status, response.statusText, Object.fromEntries(response.headers))
          if (response.body) {
            const reader = response.body.getReader()
            const pump = async (): Promise<void> => {
              const { done, value } = await reader.read()
              if (done) {
                res.end()
                return
              }
              res.write(value)
              return pump()
            }
            await pump()
          }
          else {
            res.end()
          }
        }
        catch {
          res.writeHead(502)
          res.end('Proxy error')
        }
      })
    },

    handleConnect(req: IncomingMessage, socket: Socket, head: Buffer) {
      handleConnect(config, grantsClients, req, socket, head, {
        secretsStore: deps.secretsStore,
        leafCache: deps.leafCache,
        daemonMode: deps.daemonMode,
      })
    },
  }
}

/**
 * Forward a request to the target URL.
 * Strips proxy-specific headers, preserves the rest.
 *
 * If a `secretsStore` is provided and a secret matches `targetParsed`, the
 * configured header is rendered from the entry's template and set on the
 * outgoing request (replacing any existing value with the same name).
 */
async function forwardRequest(
  originalReq: Request,
  targetUrl: string,
  targetParsed: URL,
  cachedBody: ArrayBuffer | null | undefined,
  secretsStore: SecretsStore | undefined,
  fetchImpl: UpstreamFetch,
): Promise<Response> {
  const headers = new Headers(originalReq.headers)
  // Remove proxy-specific headers
  headers.delete('proxy-authorization')
  headers.delete('proxy-connection')
  // Don't send host of the proxy
  headers.delete('host')

  // Inject hook: consult the secrets store after grant decisions and before
  // forwarding. Logs are redacted — only name/header/method/url are printed,
  // never the secret value or rendered template.
  if (secretsStore) {
    const match = secretsStore.findFor(targetParsed)
    if (match) {
      const rendered = match.template.replace(/\$\{value\}/g, match.value)
      headers.set(match.header, rendered)
      console.error(
        `[openape-proxy] injected secret '${match.name}' into ${match.header} for ${originalReq.method} ${targetUrl}`,
      )
    }
  }

  const body = cachedBody && cachedBody.byteLength > 0 ? cachedBody : null

  try {
    const upstreamReq = new Request(targetUrl, {
      method: originalReq.method,
      headers,
      body,
      duplex: 'half',
      redirect: 'manual',
    } as RequestInit)
    const res = await fetchImpl(upstreamReq)

    // Stream the response back
    const responseHeaders = new Headers(res.headers)
    // Remove hop-by-hop headers
    responseHeaders.delete('transfer-encoding')
    responseHeaders.delete('connection')

    return new Response(res.body, {
      status: res.status,
      statusText: res.statusText,
      headers: responseHeaders,
    })
  }
  catch (err) {
    const msg = err instanceof Error ? err.message : 'Upstream error'
    return new Response(`Proxy error: ${msg}`, { status: 502 })
  }
}
